import { Injectable } from '@angular/core';
import { fromEvent, interval, merge, Subject, Subscription, timer } from 'rxjs';
import { FromEventTarget } from 'rxjs/internal/observable/fromEvent';
import { RouteService } from './route.service';

@Injectable({
  providedIn: 'root',
})
export class IdleService {
  /**
   * List document interrupts
   */
  private readonly DOCUMENT_INTERRUPTS = [
    'mousemove',
    'click',
    'mousedown',
    'keypress',
    'DOMMouseScroll',
    'mousewheel',
    'touchmove',
    'MSPointerMove',
    'pointermove',
  ];

  /**
   * List window interrupts
   */
  private readonly WINDOW_INTERRUPTS = ['mousemove', 'resize'];

  /**
   * Subscriptions
   */
  private watchSubscription: Subscription | null = null;
  private timerSubscription: Subscription | null = null;
  private idleTimerSubscription: Subscription | null = null;
  private _onTimeout = new Subject();
  private _onTimeoutWarning = new Subject<number>();
  private _onIdleStart = new Subject();
  private _onIdleEnd = new Subject();
  private idleCounter = interval(1000);

  private idleStartTimeout: any;
  private timeoutWarningInterval: any;
  private idleState: 'START' | 'END' | 'INITIAL' = 'INITIAL';

  /**
   * Variables
   */
  private _idle: number = 0;
  private _idleTimeout: number = 0;
  requestCounter: number = 0;
  // titleWebWorker!: Worker;
  idleWebWorker!: Worker;
  totalIdleWebWorker!: Worker;
  ORIGINALIDLETIME: number = 0;

  constructor(private route: RouteService) {}

  /**
   * Start watching
   * @param timeoutInSec
   */
  watch = () => {
    if (this.watchSubscription) return;
    // start watching for idle
    this.watchIdle();
    this.watchSubscription = this.mergedInterrupts.subscribe(() => {
      // if idle state is not END, emit onIdleEnd to avoid multiple emits
      if (this.idleState !== 'END') this._onIdleEnd.next();
      // if interrupted, state will be END
      this.idleState = 'END';
      // if interrupted, don't emit timeout warning
      clearInterval(this.timeoutWarningInterval);
      // reset the process
      this.reset();
      // again star watching fo idleness
      this.watchIdle();
    });
    // start time for final timeout
    this.timer();
  };

  /**
   * Reset timer
   */
  reset = () => {
    clearInterval(this.timeoutWarningInterval);
    // unsubscribe everything to start over
    this.timerSubscription?.unsubscribe();
    this.watchSubscription?.unsubscribe();
    this.idleTimerSubscription?.unsubscribe();
    this.timerSubscription = null;
    this.watchSubscription = null;
    this.idleTimerSubscription = null;
    this.watch();
  };

  /**
   * Stop idle process
   */
  stop = () => {
    this.timerSubscription?.unsubscribe();
    this.watchSubscription?.unsubscribe();
    this.idleWebWorker?.terminate();
    this.idleTimerSubscription?.unsubscribe();
    clearInterval(this.timeoutWarningInterval);
    clearTimeout(this.idleStartTimeout);
  };

  /**
   * Emits on timeout
   */
  get onTimeout() {
    return this._onTimeout;
  }

  /**
   * Emits on idle start
   */
  get onIdleStart() {
    return this._onIdleStart;
  }

  /**
   * Emits on idle end
   */
  get onIdleEnd() {
    return this._onIdleEnd;
  }

  /**
   * Emits on timeout warning
   */
  get onTimeoutWarning() {
    return this._onTimeoutWarning;
  }

  /**
   * Set idle time (in seconds)
   */
  set idle(idle: number) {
    this.ORIGINALIDLETIME = idle;
    const idletime = localStorage.getItem('timeout');
    if (idletime != null) {
      this._idle = parseInt(idletime) * 1000;
    } else {
      this._idle = idle * 1000;
    }
  }

  /**
   * Set idle timeout (in seconds)
   */
  set idleTimeout(idleTimeout: number) {
    this._idleTimeout = idleTimeout * 1000;
  }

  private resetIdleTimeOutFunction = () => {
    localStorage.setItem('timeout', JSON.stringify(1));
    if (typeof Worker !== 'undefined') {
      this.idleWebWorker = new Worker(
        new URL('./../../core/web-workers/idle-worker.worker', import.meta.url),
        {
          type: 'module',
        }
      );

      this.idleWebWorker.onmessage = ({ data }) => {
        if (data.message == 'done') {
          this.idleState = 'START';
          this.onIdleStart.next();
          this.idleWebWorker.terminate();

          // if idling, emit timeout warning
          this.emitTimeoutWarning();
        } else if (data.message == 'not_completed') {
          let t = localStorage.getItem('timeout') as unknown as number;
          if (t == null) {
            t = 0;
            localStorage.setItem('timeout', JSON.stringify(t));
            this.idleWebWorker?.terminate();
            this.watchIdle();
            this.timerSubscription?.unsubscribe();
            this.timer();
          } else {
            t = data?.secondCounter;
            localStorage.setItem('timeout', JSON.stringify(data.secondCounter));
          }
          this.idleWebWorker.postMessage({
            secondCounter: t,
            type: 'idleTimeOut',
            idleTimeOut: this._idle,
          });
        }
      };
      this.idleWebWorker.onerror = (err) => {
        console.log('error', err);
      };

      this.idleWebWorker.postMessage({
        secondCounter: 1,
        type: 'idleTimeOut',
        idleTimeOut: this._idle,
      });
      // requestCounter: this.requestCounter,
    }
  };

  /**
   * Watch for idle
   */
  private watchIdle = () => {
    localStorage.removeItem('timeout');
    this.idleWebWorker?.terminate();
    this.idle = this.ORIGINALIDLETIME;
    clearTimeout(this.idleStartTimeout);
    this.idleStartTimeout = setTimeout(() => {
      this.resetIdleTimeOutFunction();
      clearTimeout(this.idleStartTimeout);
    }, 1000);

    // this.idleTimerSubscription?.unsubscribe();
    // this.idleTimerSubscription = this.idleCounter
    //   .pipe(take(this._idle / 1000))
    //   .subscribe((e: any) => {
    //     if (e + 1 == this._idle / 1000) {
    //       this.idleState = 'START';
    //       this.onIdleStart.next();
    //       // if idling, emit timeout warning
    //       this.emitTimeoutWarning();
    //     }
    //   });
    // clearTimeout(this.idleStartTimeout);

    // // emit idleStart if not interupted
    // this.idleStartTimeout = setTimeout(() => {
    // this.idleState = 'START';
    // this.onIdleStart.next();

    //   // if idling, emit timeout warning
    //   this.emitTimeoutWarning();
    // }, this._idle);
  };

  /**
   * Emit timeout warning once idle starts
   */
  private emitTimeoutWarning = () => {
    clearInterval(this.timeoutWarningInterval);
    // emit timeout warning in 1 sec interval
    let secondsUp = 0;
    this._onTimeoutWarning.next(this._idleTimeout / 1000 - secondsUp++);

    if (typeof Worker !== 'undefined') {
      this.idleWebWorker = new Worker(
        new URL('./../../core/web-workers/idle-worker.worker', import.meta.url),
        {
          type: 'module',
        }
      );

      this.idleWebWorker.onmessage = ({ data }) => {
        const time = localStorage.getItem('timeout') as unknown as number;
        if (time == null || time < this._idle / 1000 - 1) {
          this.idleWebWorker.terminate();
          this.route.getApplicationName();
          this.watchIdle();
          // this.resetIdleTimeOutFunction();
          this.timerSubscription?.unsubscribe();
          this.timer();
        } else {
          if (this._idleTimeout / 1000 - secondsUp == 0)
            this.idleWebWorker.terminate();
          this._onTimeoutWarning.next(this._idleTimeout / 1000 - secondsUp++);
        }
        // this.timeoutWarningInterval = setInterval(() => {
        // }, 1000);
      };
      this.idleWebWorker.onerror = (err) => {
        console.log('error', err);
      };

      this.idleWebWorker.postMessage({
        type: 'setTitle',
        idleTimeOut: this._idle,
      });
    }
  };

  /**
   * Start timer
   */
  private timer = () => {
    // start for final timeout
    this.timerSubscription = timer(
      this._idleTimeout + this._idle,
      this._idleTimeout
    ).subscribe(() => {
      // clear everything
      this.stop();
      this._onTimeout.next();
    });
  };

  /**
   * Document & Window merged interrupt observable
   */
  private get mergedInterrupts() {
    return merge(
      ...this.documentInteruptsObservable,
      ...this.windowInteruptsObservable
    );
  }

  /**
   * Document level interrupts observable
   */
  private get documentInteruptsObservable() {
    return this.toObservableArray(document, this.DOCUMENT_INTERRUPTS);
  }

  /**
   * Window level interrupts observable
   */
  private get windowInteruptsObservable() {
    return this.toObservableArray(window, this.WINDOW_INTERRUPTS);
  }

  /**
   * Make observable array using interrupts
   * @param target
   * @param interrupts
   * @returns
   */
  private toObservableArray = (
    target: FromEventTarget<Event>,
    interrupts: Array<string>
  ) => interrupts.map((i) => fromEvent(target, i));
}
